import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Download, Upload, LogOut, ChevronRight, Users, UserSearch, Layers, Database, Boxes, HardDrive, SlidersHorizontal, Wrench, CloudDownload, FolderInput } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider.js';

interface LayoutProps {
  children: React.ReactNode;
}

function NavItem({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer select-none transition-colors ${
          isActive
            ? 'bg-[#efeae1] text-[#37352f] font-medium'
            : 'text-[#73726e] hover:bg-[#efeae1] hover:text-[#37352f]'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <ChevronRight
            size={12}
            className={`shrink-0 transition-transform ${isActive ? 'rotate-90 text-[#37352f]' : 'text-[#aeaaa4]'}`}
          />
          <Icon size={14} className="shrink-0" />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

export function Layout({ children }: LayoutProps) {
  const { user, userRecord, signOut } = useAuth();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate('/');
  }

  const initials = user?.displayName
    ? user.displayName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  return (
    <div className="flex h-screen overflow-hidden bg-[#fdfcfa]">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 flex flex-col border-r border-[#eae5dd] bg-[#F6F4F2]">
        {/* Workspace header */}
        <div className="flex items-center gap-2 px-3 py-3 border-b border-[#eae5dd]">
          <img src="/atlas-icon.png" alt="Migration Tools" className="w-5 h-5 rounded shrink-0" />
          <span className="text-sm font-semibold text-[#37352f] truncate">
            Migration Tools
          </span>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5 overflow-y-auto">
          <NavItem to="/ftp-import" icon={Download} label="Imports" />
          <NavItem to="/mount" icon={Layers} label="Mount" />
          <NavItem to="/agency-users" icon={UserSearch} label="Agency users" />
          <NavItem to="/redshift-export" icon={CloudDownload} label="Redshift export" />
          <NavItem to="/move-attachments" icon={FolderInput} label="Move attachments" />

          {userRecord?.isAdmin && (
            <>
              <div className="mt-4 mb-1 px-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#aeaaa4]">Admin</span>
              </div>
              <NavItem to="/exports" icon={Upload} label="Exports" />
              <NavItem to="/tools" icon={Wrench} label="Tools" />

              <div className="mt-4 mb-1 px-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#aeaaa4]">Configuration</span>
              </div>
              <NavItem to="/team" icon={Users} label="Team" />
              <NavItem to="/crms" icon={Boxes} label="CRMs" />
              <NavItem to="/export-settings" icon={SlidersHorizontal} label="Export settings" />
              <NavItem to="/rds-databases" icon={Database} label="RDS databases" />
              <NavItem to="/settings" icon={HardDrive} label="Destination" />
            </>
          )}
        </nav>

        {/* User */}
        <div className="px-2 py-3 border-t border-[#eae5dd]">
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-[#eae4da] transition-colors group"
          >
            <div className="w-6 h-6 rounded-full bg-[#e8e0d6] flex items-center justify-center shrink-0">
              <span className="text-[10px] font-medium text-[#73726e]">
                {initials}
              </span>
            </div>
            <div className="flex-1 text-left overflow-hidden">
              <p className="text-xs font-medium text-[#37352f] truncate">
                {user?.displayName ?? user?.email ?? 'Unknown'}
              </p>
              <p className="text-[10px] text-[#aeaaa4] truncate">
                {user?.email}
              </p>
            </div>
            <LogOut
              size={12}
              className="shrink-0 text-[#aeaaa4] opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}
